/**
 * Common permission utilities for meal plan editing
 * Used by both PRO and consumer code
 */

import { inquire } from '@utils/Enforcer';

/**
 * Check if user can edit meals (add, swap, remove)
 * @param {Object} profile - User profile with preferences
 * @returns {boolean}
 */
export const canEditMeals = (profile) => {
    if (!profile) return false;

    const { inhibit_swap = false, rd_override = false } = profile;

    // RD override allows editing even if swap is inhibited
    return !inhibit_swap || rd_override;
};

/**
 * Check if user can view nutrition information
 * @param {Object} profile - User profile with preferences
 * @returns {boolean}
 */
export const canViewNutrition = (profile) => {
    if (!profile) return false;

    const { hide_nutrition = false, rd_override = false } = profile;

    // RD override allows viewing even if nutrition is hidden
    return !hide_nutrition || rd_override;
};

/**
 * Check if user can save/update a plan
 * @param {Object} params - Parameters for permission check
 * @param {boolean} params.canWrite - Write permission from server
 * @param {Object} params.plan - The meal plan object
 * @param {Object} params.user - Current user object
 * @returns {boolean}
 */
export const canSavePlan = ({ canWrite, plan, user }) => {
    if (!user || !plan) return false;

    // User must have write permission
    if (!canWrite) return false;

    // If user owns the plan, they can save
    if (plan.owner === user.uuid) return true;

    // If plan has no owner (new plan), user can save
    if (!plan.owner) return true;

    // Otherwise, they need to copy the plan
    return false;
};

/**
 * Check if user needs to copy plan before editing
 * @param {Object} plan - The meal plan object
 * @param {Object} user - Current user object
 * @returns {boolean}
 */
export const shouldCopyPlan = (plan, user) => {
    if (!plan || !user) return false;

    // If plan has no owner, no need to copy
    if (!plan.owner) return false;

    // User doesn't own the plan
    return plan.owner !== user.uuid;
};

/**
 * Check if user can create new plans
 * @param {boolean} canCreate - Create permission from server
 * @param {Object} plan - Current plan object
 * @returns {boolean}
 */
export const canCreateNewPlan = (canCreate, plan) => {
    // User must have create permission OR be working with a virtual plan
    return canCreate || (plan && !plan.links?.self);
};

/**
 * Check if user can recommend plans to patients
 * @param {Object} params - Parameters for permission check
 * @param {boolean} params.canPublish - Publish permission from server
 * @param {Object} params.user - Current user object
 * @returns {boolean}
 */
export const canRecommendPlan = ({ canPublish, user }) => {
    if (!user) return false;

    // Must have publish permission and be a dietitian
    return canPublish && user.dietitian;
};

/**
 * Check if user has premium access
 * @param {Object} user - Current user object
 * @returns {boolean}
 */
export const hasPremiumAccess = (user) => {
    if (!user) return false;

    return user.capabilities?.meal_planner || false;
};

/**
 * Get comprehensive permissions for a user
 * @param {Object} params - All permission-related data
 * @returns {Object} Object with all permission flags
 */
export const getPermissions = ({ user, profile, plan, canWrite, canPublish, canCreate }) => {
    return {
        // Basic permissions
        canWrite,
        canPublish,
        canCreate,

        // Derived permissions
        canEditMeals: canEditMeals(profile),
        canViewNutrition: canViewNutrition(profile),
        canSavePlan: canSavePlan({ canWrite, plan, user }),
        shouldCopyPlan: shouldCopyPlan(plan, user),
        canCreateNewPlan: canCreateNewPlan(canCreate, plan),
        canRecommendPlan: canRecommendPlan({ canPublish, user }),
        hasPremiumAccess: hasPremiumAccess(user),

        // User type flags
        isDietitian: user?.dietitian || false,
        isPatientView: profile?.type === 'patient' || profile?.type === 'alternate-patient',
    };
};

/**
 * Fetch permissions from the server for a specific plan
 * @param {string} planUuid - The UUID of the plan (can be null for new plans)
 * @returns {Promise<Object>} Promise resolving to permissions object
 */
export const fetchPlanPermissions = async (planUuid) => {
    // Build inquiries array
    const inquiries = [];
    
    // Only check plan-specific permissions if we have a UUID
    if (planUuid) {
        inquiries.push(
            {
                action: 'publish',
                resource: planUuid,
            },
            {
                action: 'write',
                resource: planUuid,
            }
        );
    }
    
    // Always check if user can create new plans
    inquiries.push({
        action: 'write',
        resource: '/plans'
    });
    
    // Make the inquiry
    const decisions = await inquire(inquiries);
    
    // Process decisions
    let canPublish = false;
    let canWrite = false;
    let canCreate = false;
    
    if (planUuid) {
        const publishDecision = decisions.find(d => d.action === 'publish' && d.resource === planUuid);
        const writeDecision = decisions.find(d => d.action === 'write' && d.resource === planUuid);
        canPublish = publishDecision && publishDecision.decision === 'permit';
        canWrite = writeDecision && writeDecision.decision === 'permit';
    }
    
    const createDecision = decisions.find(d => d.action === 'write' && d.resource === '/plans');
    canCreate = createDecision && createDecision.decision === 'permit';
    
    return {
        canPublish,
        canWrite,
        canCreate
    };
};
